import { useEffect, useMemo, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { type DegradationCallback } from '@atlassian/search-client/degradable-promise';
import {
	get3pSearchConfig,
	getSearchConfigV2,
	getUserLoomAuthStatus,
	type TProductDataRegions,
	type TProductEdition,
	type TRovoFeatures,
	type TSearch3pConfigProps,
	type TSearchConfigAPIResponse,
} from '@atlassian/search-client/fetch-search-config';
import {
	type CacheAnalyticsCallback,
	type NetworkCallbacks,
} from '@atlassian/search-client/persist-refresh';
import { searchLinkedSitesQuery } from '@atlassian/search-client/search-page';
import { type LinkedSite } from '@atlassian/search-client/search-page/types';

import { getJiraIssueIndexedForTenant } from '../../common/constants/get-jira-issue-indexed-for-tenant';
import { getProductDataRegions } from '../../common/constants/get-product-data-regions';
import { getProductEdition } from '../../common/constants/get-product-edition';
import { getRovoFeatures } from '../../common/constants/get-rovo-features';
import { isNounsAvailable } from '../../common/constants/is-nouns-available';
import { isRovoEnabled } from '../../common/constants/is-rovo-enabled';
import { PrimaryProductKeys } from '../../common/constants/primary-product-keys';
import {
	ARI_EXTRACTED_1P_KEYS,
	NounKeys,
	type PrimaryProductKey,
	type ProductKeys,
	ProductKeys1P,
	type SearchStoreExperience,
} from '../../common/constants/types';
import { getFirstCloudId } from '../../common/constants/utils';
import {
	type ConnectorSource,
	FEDERATED_CONNECTOR_SOURCE,
	type ForgeConnectorProductKey,
	FULL_CONNECTOR_SOURCE,
	isForgeConnectorProductKey,
	LITE_CONNECTOR_SOURCE,
	SMARTLINKS_CONNECTOR_SOURCE,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../common/types/schemas/types';
import {
	ProductKeys3P,
	type ProductKeys3P as ProductKeys3PType,
} from '../../common/ui/configs/types';
import { ThirdPartyConfigs } from '../../common/ui/configs/utils';
import {
	onDegradedDependency,
	onFailedDependency,
	onRequestCacheAccess,
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
} from '../../common/utils/analytics';
import { CONFIG_TASK_SUFFIXES } from '../../common/utils/analytics/config-task-suffixes';
import {
	buildTaskName,
	type ConfigFetchAnalytics,
	type FireAnalyticsEvent,
} from '../../common/utils/analytics/types';
import { mapForgeConnectorToThirdPartyConfig } from '../combined-config/forge';

const EXTRACT_PRODUCT = /ari:cloud:(.*)?::/;
const BITBUCKET_WORKSPACE_ARI = 'ari:cloud:bitbucket::workspace';
const FEDERATED_NO_OAUTH_PRODUCT_KEYS = new Set<ProductKeys3PType>(['servicenow', 'zendesk']);

/**
 * Normalizes product names from ARIs to match our product keys.
 * Backend uses 'jira-software' in ARIs but we use 'jira' as the product key.
 */
const normalizeProductFromAri = (ariProduct: string): string => {
	if (ariProduct === 'jira-software') {
		return ProductKeys1P.Jira;
	}
	return ariProduct;
};

/**
 * Nouns configuration (simplified - no icons for now)
 */
const supportedNouns: Record<string, unknown> = {
	[NounKeys.project]: {},
	[NounKeys.goal]: {},
	[NounKeys.AtlassianWebCrawler]: {},
};

/**
 * All product keys - 1P combined with 3P
 */
const allProductKeys: (ProductKeys3PType | ForgeConnectorProductKey)[] = [
	...Object.values(ARI_EXTRACTED_1P_KEYS),
	...(ThirdPartyConfigs.keys() as ProductKeys3PType[]),
];

/**
 * Factory function type for creating network callbacks.
 * Used for network-level vital stats tracking.
 */
export type CreateNetworkCallbacksFn = (
	taskId: string,
	taskName: string,
	searchSessionId: string,
	fireAnalyticsEvent: (event: FireAnalyticsEvent) => void,
	taskStartAt: number,
	context?: Record<string, unknown>,
) => NetworkCallbacks;

/**
 * Required analytics and logging configuration for useFetchConfig.
 * Consumers MUST provide these to enable proper analytics tracking.
 *
 * Example usage with FPS:
 * ```typescript
 * const { fireAnalyticsEvent } = useAnalytics();
 * const searchSessionId = useSearchSessionId();
 * const logException = useLogException();
 *
 * useFetchConfig({
 *   cloudId,
 *   analytics: {
 *     searchSessionId,
 *     source: 'fullPageSearch',
 *     fireAnalyticsEvent,
 *     logException,
 *   },
 *   // ...other props
 * });
 * ```
 */
export interface UseFetchConfigAnalytics extends ConfigFetchAnalytics {
	/**
	 * Optional factory to create network callbacks for vital stats tracking.
	 * If provided, enables network-level vital stats events.
	 */
	createNetworkCallbacks?: CreateNetworkCallbacksFn;
}

export type UseFetchConfigProps = {
	thirdPartyAbsoluteUrl?: string;
	thirdPartyDisabled?: boolean;
	userId?: string | null;
	cloudId?: string;
	aggAbsoluteUrl?: string;
	primaryProduct?: PrimaryProductKey;
	/**
	 * When passed this would be used as is to invoke the endpoint which returns search-configuration.
	 */
	configFetchAbsoluteUrl?: string;
	aggregatorHostName?: string;
	multiSiteSearchInRovo?: boolean;
	experience?: SearchStoreExperience;
	/**
	 * Required analytics configuration.
	 * All consumers must provide searchSessionId, source, fireAnalyticsEvent, and logException.
	 */
	analytics: UseFetchConfigAnalytics;
};

/**
 * Some products (eg. Loom) have separate auth checks that need to be enforced before
 * returning the final list of accessible products.
 *
 * Remove when cleaning up fg('rovo-search-combined-loom-auth')
 */
const lastMileProductChecks = (isUserLoomAuthed: boolean, products?: ProductKeys[]) => {
	if (!products || products.length === 0) {
		return products;
	}

	return products.filter((product) => isUserLoomAuthed || product !== ProductKeys3P.Loom);
};

/**
 * A React hook which handles fetching of all the config necessary to bootstrap 1P + 3P results.
 *
 * NOTE (RAOC-22): A new canonical hook `useRovoConfig` is being introduced at
 * `@atlassian/rovo-connectors-config/hooks/useRovoConfig`. It is **not yet** the
 * supported replacement for this hook — `useRovoConfig` is currently being
 * implemented, tested, and rolled out behind the `rovo_fps_use_rovo_config`
 * feature gate. Once `useRovoConfig` has been validated end-to-end and the
 * rollout is complete, this hook will be marked `@deprecated` and ultimately
 * removed.
 */
export const useFetchConfig = ({
	cloudId: cloudIdProp,
	configFetchAbsoluteUrl,
	aggregatorHostName,
	userId,
	thirdPartyAbsoluteUrl,
	thirdPartyDisabled,
	primaryProduct,
	aggAbsoluteUrl,
	multiSiteSearchInRovo,
	experience,
	analytics,
}: UseFetchConfigProps): {
	accessibleProducts: string[] | undefined;
	accessibleNouns: ('atlassian-web-crawler' | 'project' | 'goal')[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>> | undefined;
	firstCloudId: string;
	intercomHmac: string | undefined;
	isNounsAvailable: boolean | undefined;
	isRovoEnabled: boolean | undefined;
	isJiraIssueIndexed: boolean | undefined;
	productEdition: TProductEdition | undefined;
	productDataRegions: TProductDataRegions | undefined;
	rovoFeatures: TRovoFeatures | undefined;
	isConfigLoaded: boolean;
	linkedCloudARIs: string[] | undefined;
} => {
	const {
		fireAnalyticsEvent,
		logException,
		searchSessionId,
		source,
		actionSubject,
		taskNamePrefix,
		createNetworkCallbacks,
	} = analytics;

	const [firstPartyResponse, setFirstPartyResponse] = useState<TSearchConfigAPIResponse>();
	const [isConfigLoaded, setIsConfigLoaded] = useState<boolean>(false);

	// Loom requires a separate auth check
	const [isUserLoomAuthed, setIsUserLoomAuthed] = useState<boolean>(false);

	// Rovo Multiple Site Search
	const [linkedCloudARIs, setLinkedCloudARIs] = useState<string[] | undefined>(undefined);

	const [accessibleProductsConfig, setAccessibleProductsConfig] = useState<{
		products?: ProductKeys[];
		nouns: (typeof NounKeys)[keyof typeof NounKeys][];
		thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	}>({
		nouns: [],
	});

	const firstCloudId = useMemo(() => {
		if (!cloudIdProp && firstPartyResponse) {
			return getFirstCloudId(firstPartyResponse);
		}
		return '';
	}, [firstPartyResponse, cloudIdProp]);

	const thirdPartyResponse = useThirdPartyConfig({
		cloudId: cloudIdProp || firstCloudId || '',
		userId: userId,
		absoluteUrl: thirdPartyAbsoluteUrl,
		disabled: thirdPartyDisabled,
		experience,
		analytics,
	});

	// Rovo Multiple Site Search - Store linked site ARIs in state
	useEffect(() => {
		const cloudId = cloudIdProp || firstCloudId;
		if (cloudId && multiSiteSearchInRovo) {
			searchLinkedSitesQuery({
				variables: { id: `ari:cloud:confluence::site/${cloudId}` },
				aggAbsoluteUrl,
			})
				.then((response: any) => {
					const edges = response?.data?.collabContext_workspaceIsConnectedToWorkspace?.edges ?? [];
					const linkedARIs = edges.map((edge: LinkedSite) => edge.node.id);

					const currentCloudIdARI = `ari:cloud:confluence::site/${cloudId}`;
					const currentIndex = linkedARIs.findIndex((ari: string) => ari === currentCloudIdARI);

					if (currentIndex > 0) {
						linkedARIs.splice(currentIndex, 1);
						linkedARIs.unshift(currentCloudIdARI);
					}

					setLinkedCloudARIs(linkedARIs);
				})
				.catch((error: Error) => {
					logException(error);
				});
		}
	}, [cloudIdProp, firstCloudId, multiSiteSearchInRovo, logException, aggAbsoluteUrl]);

	// Loom auth check
	useEffect(() => {
		if (!fg('rovo-search-combined-loom-auth') && !isUserLoomAuthed) {
			if (
				primaryProduct === PrimaryProductKeys.Confluence ||
				primaryProduct === PrimaryProductKeys.Atlas ||
				((primaryProduct === PrimaryProductKeys.Jira ||
					primaryProduct === PrimaryProductKeys.Focus) &&
					fg('search_loom_auth_check_in_jira_mercury'))
			) {
				getUserLoomAuthStatus({
					userId,
					cloudId: cloudIdProp,
				})
					.then((response) => setIsUserLoomAuthed(response?.status === 'mastered'))
					.catch((error) => {
						logException(error);
						setIsUserLoomAuthed(false);
					});
			} else {
				setIsUserLoomAuthed(true);
			}
		}
	}, [isUserLoomAuthed, setIsUserLoomAuthed, userId, logException, cloudIdProp, primaryProduct]);

	// Fetch 1P config
	useEffect(() => {
		if (!firstPartyResponse) {
			const taskStartAt = performance.now();
			const taskId = crypto.randomUUID();

			const degradationCallback: DegradationCallback = (reason) => {
				fireAnalyticsEvent(
					onDegradedDependency({
						dependency: 'search-config',
						searchSessionId,
						reason,
						source,
						actionSubject,
					}),
				);
			};

			const cacheAnalyticsCallback: CacheAnalyticsCallback = async (state) => {
				fireAnalyticsEvent(
					onRequestCacheAccess({
						requestName: 'search-config',
						searchSessionId,
						cacheHit: state.hit,
						refreshTriggered: state.refreshTriggered,
						source,
						actionSubject,
					}),
				);
			};

			const search1PConfigTTL = 1000 * 60 * 15; // 15 minutes

			const networkCallbacks = createNetworkCallbacks
				? createNetworkCallbacks(
						taskId,
						buildTaskName(taskNamePrefix, CONFIG_TASK_SUFFIXES.FIRST_PARTY_CONFIG_NETWORK),
						searchSessionId,
						fireAnalyticsEvent,
						taskStartAt,
						{},
					)
				: undefined;

			const searchConfigParams = {
				cloudId: cloudIdProp,
				absoluteUrl: configFetchAbsoluteUrl,
				hostName: aggregatorHostName,
				userId,
				degradationCallback,
				cacheAnalyticsCallback,
				expiration: search1PConfigTTL,
				returnExpired: true,
				onlyRefreshIfExpired: true,
				networkCallbacks,
			};

			fireAnalyticsEvent(
				onTaskStart({
					searchSessionId,
					taskId,
					task: buildTaskName(taskNamePrefix, CONFIG_TASK_SUFFIXES.FIRST_PARTY_CONFIG_UX),
					moreContext: { experience },
				}),
			);

			getSearchConfigV2(searchConfigParams)
				.then((response) => {
					setFirstPartyResponse(response);

					fireAnalyticsEvent(
						onTaskSuccess({
							searchSessionId,
							taskId,
							task: buildTaskName(taskNamePrefix, CONFIG_TASK_SUFFIXES.FIRST_PARTY_CONFIG_UX),
							moreContext: { timeTaken: performance.now() - taskStartAt, experience },
						}),
					);
				})
				.catch((error) => {
					logException(error);

					fireAnalyticsEvent(
						onFailedDependency({
							dependency: 'search-config',
							searchSessionId,
							source,
							actionSubject,
						}),
					);

					fireAnalyticsEvent(
						onTaskFail({
							searchSessionId,
							taskId,
							task: buildTaskName(taskNamePrefix, CONFIG_TASK_SUFFIXES.FIRST_PARTY_CONFIG_UX),
							moreContext: {
								error: error.name,
								timeTaken: performance.now() - taskStartAt,
								experience,
							},
						}),
					);

					setFirstPartyResponse({
						resources: [],
						intercomHmac: '',
						siteMetadata: [],
					});
				});
		}
	}, [
		firstPartyResponse,
		setFirstPartyResponse,
		cloudIdProp,
		configFetchAbsoluteUrl,
		aggregatorHostName,
		userId,
		logException,
		fireAnalyticsEvent,
		searchSessionId,
		source,
		actionSubject,
		taskNamePrefix,
		createNetworkCallbacks,
		experience,
	]);

	// Combine 1P and 3P config
	useEffect(() => {
		if (
			thirdPartyResponse.productKeys &&
			firstPartyResponse &&
			!accessibleProductsConfig.products
		) {
			let allAccessibleProducts: ProductKeys[] = [];

			const firstPartyProducts = [
				...new Set(
					firstPartyResponse.resources.flatMap((resource) => {
						const { id } = resource;
						const match = id.match(EXTRACT_PRODUCT);

						if (match && match.length >= 2) {
							const normalizedProduct = normalizeProductFromAri(match[1]);
							if ((ARI_EXTRACTED_1P_KEYS as readonly string[]).includes(normalizedProduct)) {
								return [normalizedProduct];
							}
							// Loom delivers a 1P-style ARI (ari:cloud:loom::site/...) but is classified
							// as a 3P product (ProductKeys3P.Loom).
							if (normalizedProduct === ProductKeys3P.Loom) {
								return [normalizedProduct];
							}
						}

						return [];
					}),
				),
			];

			if (firstPartyProducts.length === 0 && primaryProduct) {
				allAccessibleProducts.push(primaryProduct as ProductKeys);
			} else {
				allAccessibleProducts.push(...(firstPartyProducts as ProductKeys[]));
			}

			const areNounsEnabledThroughLegacyFlag = isNounsAvailable(firstPartyResponse, cloudIdProp);

			const accessibleNouns = areNounsEnabledThroughLegacyFlag
				? []
				: firstPartyResponse.resources.flatMap((resource) => {
						const { id } = resource;
						const match = id.match(EXTRACT_PRODUCT);

						if (match && match.length >= 2 && match[1] in supportedNouns) {
							return [match[1] as (typeof NounKeys)[keyof typeof NounKeys]];
						}

						return [];
					});

			if (accessibleNouns.length > 0 && !areNounsEnabledThroughLegacyFlag) {
				allAccessibleProducts.push(ProductKeys1P.Atlas);
			}

			if (
				allAccessibleProducts.includes('bitbucket') &&
				!firstPartyResponse.resources.find((r) => r.id.includes(BITBUCKET_WORKSPACE_ARI))
			) {
				allAccessibleProducts = allAccessibleProducts.filter(
					(product) => product !== ProductKeys1P.Bitbucket,
				);
			}

			const doesUserHaveRovo = isRovoEnabled(firstPartyResponse, cloudIdProp);
			if (doesUserHaveRovo) {
				const thirdPartyProductKeys = thirdPartyResponse.productKeys || [];
				allAccessibleProducts.push(...(thirdPartyProductKeys as ProductKeys[]));
				setAccessibleProductsConfig({
					products: allAccessibleProducts,
					nouns: accessibleNouns,
					thirdPartyConfigs: thirdPartyResponse.thirdPartyConfigs,
				});
			} else {
				setAccessibleProductsConfig({
					products: allAccessibleProducts,
					nouns: accessibleNouns,
					thirdPartyConfigs: {},
				});
			}
			setIsConfigLoaded(true);
		}
	}, [
		isUserLoomAuthed,
		thirdPartyResponse,
		firstPartyResponse,
		setAccessibleProductsConfig,
		primaryProduct,
		fireAnalyticsEvent,
		logException,
		cloudIdProp,
		accessibleProductsConfig.products,
	]);

	const accessibleProducts = fg('rovo-search-combined-loom-auth')
		? accessibleProductsConfig.products
		: lastMileProductChecks(isUserLoomAuthed, accessibleProductsConfig.products);

	return {
		accessibleProducts,
		accessibleNouns: accessibleProductsConfig.nouns,
		thirdPartyConfigs: accessibleProductsConfig.thirdPartyConfigs,
		firstCloudId,
		intercomHmac: firstPartyResponse?.intercomHmac,
		isNounsAvailable: isNounsAvailable(firstPartyResponse, cloudIdProp),
		isRovoEnabled: isRovoEnabled(firstPartyResponse, cloudIdProp),
		isJiraIssueIndexed: getJiraIssueIndexedForTenant(firstPartyResponse, cloudIdProp),
		productEdition: getProductEdition(firstPartyResponse, cloudIdProp),
		productDataRegions: getProductDataRegions(firstPartyResponse, cloudIdProp),
		rovoFeatures: getRovoFeatures(firstPartyResponse, cloudIdProp),
		isConfigLoaded,
		linkedCloudARIs,
	};
};

// ---- Third-party config hook ----

export const getThirdPartyTypes = async (): Promise<Record<string, string>> => {
	const oldThirdPartyTypes: Record<string, string> = {
		'ai-3p-connector:sharepoint-domain': 'sharepoint',
		'ai-3p-connector:google-workspace': 'drive',
	};

	const thirdPartyTypes: Record<string, string> = {
		...oldThirdPartyTypes,
		'lite-connector-gdrive': 'drive',
		'smartlinks-connector-gdrive': 'drive',
		'smartlinks-connector-gdrive-rsl': 'drive',
		'smartlinks-connector-salesforce': 'salesforce',
		'smartlinks-connector-airtable': 'airtable',
		'smartlinks-connector-figma': 'figma',
		'smartlinks-connector-gitlab': 'gitlab',
		'smartlinks-connector-miro': 'miro',
		'smartlinks-connector-asana': 'asana',
		'smartlinks-connector-smartsheet': 'smartsheet',
		'smartlinks-connector-dropbox': 'dropbox',
		'smartlinks-connector-box': 'box',
		'smartlinks-connector-lucid': 'lucid',
		'smartlinks-connector-hubspot': 'hubspot',
		'smartlinks-connector-docusign': 'docusign',
		'smartlinks-connector-slack': 'slack',
		'smartlinks-connector-teams': 'teams',
		'smartlinks-connector-azure-devops': 'azure-devops',
		'smartlinks-connector-notion': 'notion',
		'smartlinks-connector-onedrive': 'onedrive',
		'smartlinks-connector-sharepoint': 'sharepoint',
	};

	await ThirdPartyConfigs.forEach((config) => {
		if (config.resourceType) {
			thirdPartyTypes[config.resourceType] = config.id;
		}
	});

	return thirdPartyTypes;
};

type ConnectorSourceMap = Map<string, Set<ConnectorSource>>;

export const get3pProductKeys = async ({
	cloudId,
	absoluteUrl,
	userId,
	degradationCallback,
	cacheAnalyticsCallback,
	networkCallbacks,
}: TSearch3pConfigProps): Promise<{
	productKeys: string[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>;
}> => {
	const THIRD_PARTY_TYPES: Record<string, string> = await getThirdPartyTypes();

	const searchConfigParams = {
		cloudId,
		absoluteUrl,
		userId,
		degradationCallback,
		cacheAnalyticsCallback,
		expiration: 1000 * 60, // 1 minute
		returnExpired: true,
		onlyRefreshIfExpired: true,
		networkCallbacks,
	};

	const resources = await get3pSearchConfig(searchConfigParams);

	const connectorSourceMap: ConnectorSourceMap = new Map();

	let productKeys: (ProductKeys3PType | ForgeConnectorProductKey)[] = [];
	let thirdPartyConfigsWithAPIResponse: ThirdPartyConfigsBootstrap = {};

	for (const resource of resources) {
		if (isForgeConnectorProductKey(resource.type) && fg('rovo-search-dynamic-forge-config')) {
			const config = mapForgeConnectorToThirdPartyConfig(resource);
			if (config) {
				const productKey = resource.type as ForgeConnectorProductKey;
				productKeys.push(productKey);
				thirdPartyConfigsWithAPIResponse[productKey] = config;
			}
			continue;
		}

		const productKey = (THIRD_PARTY_TYPES[resource.type] || '') as ProductKeys3PType;
		if (!productKey) {
			continue;
		}

		const { providerId, datasourceId } = resource;
		productKeys.push(productKey);
		let config = {} as ThirdPartyConfigsWithAPIResponse;
		const staticConfig = await ThirdPartyConfigs.get(productKey);
		if (staticConfig) {
			const isFederatedNoOAuthConnector =
				FEDERATED_NO_OAUTH_PRODUCT_KEYS.has(productKey) &&
				resource.connectorSyncType === FEDERATED_CONNECTOR_SOURCE;

			let sourceType: ConnectorSource;

			if (resource.type.startsWith(SMARTLINKS_CONNECTOR_SOURCE)) {
				sourceType = SMARTLINKS_CONNECTOR_SOURCE;
			} else if (resource.type.startsWith(LITE_CONNECTOR_SOURCE)) {
				sourceType = LITE_CONNECTOR_SOURCE;
			} else if (isFederatedNoOAuthConnector) {
				sourceType = FEDERATED_CONNECTOR_SOURCE;
			} else if (staticConfig.isFederated) {
				sourceType = FEDERATED_CONNECTOR_SOURCE;
			} else {
				sourceType = FULL_CONNECTOR_SOURCE;
			}

			connectorSourceMap.set(
				providerId,
				(connectorSourceMap.get(providerId) || new Set()).add(sourceType),
			);

			const existingConfig = thirdPartyConfigsWithAPIResponse[productKey];
			const isExistingConfigFullConnectorSource =
				existingConfig?.connectorSources?.includes(FULL_CONNECTOR_SOURCE) || false;

			const outboundAuthUrl = isFederatedNoOAuthConnector
				? ''
				: isExistingConfigFullConnectorSource && existingConfig?.outboundAuthUrl
					? existingConfig.outboundAuthUrl
					: resource.outboundAuthUrl;

			const userNeedsOAuth = isFederatedNoOAuthConnector
				? false
				: isExistingConfigFullConnectorSource && existingConfig
					? existingConfig.userNeedsOAuth
					: !resource.isUserOAuthed;

			config = {
				...staticConfig,
				outboundAuthUrl,
				userNeedsOAuth,
				providerId,
				datasourceId,
				connectionId: resource.connectionId,
				workspaceName: resource.workspaceName,
				workspaceUrl: resource.workspaceUrl,
				connectorSources: Array.from(connectorSourceMap.get(providerId) || []),
				impersonationEnabled: resource.isImpersonationAuthEnabled,
			};

			const datasourceList = [];
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { datasources, ...configWithoutDatasources } = config;
			configWithoutDatasources.datasourceId = resource.datasourceId;
			configWithoutDatasources.connectionId = resource.connectionId;
			const datasource = {
				...configWithoutDatasources,
				...resource,
				outboundAuthUrl: isFederatedNoOAuthConnector ? '' : resource.outboundAuthUrl,
				userNeedsOAuth: isFederatedNoOAuthConnector ? false : !resource.isUserOAuthed,
				integrationAri: resource.integrationId || configWithoutDatasources.integrationAri,
			};
			datasourceList.push(datasource);
			const extraDatasources = thirdPartyConfigsWithAPIResponse[productKey]?.datasources || [];
			const cleanedExtraDatasources = extraDatasources.map(
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				({ datasources, ...configWithoutDatasources }) => configWithoutDatasources,
			);
			datasourceList.push(...cleanedExtraDatasources);
			config.datasources = datasourceList as ThirdPartyConfigsWithAPIResponse[];
			thirdPartyConfigsWithAPIResponse[productKey] = config;
		}
	}

	const filteredProductKeys = productKeys.filter((productKey) => {
		if (isForgeConnectorProductKey(productKey)) {
			return true;
		}
		return allProductKeys.includes(productKey as ProductKeys3PType);
	});

	return { productKeys: filteredProductKeys, thirdPartyConfigs: thirdPartyConfigsWithAPIResponse };
};

const EMPTY_RESPONSE: (ProductKeys3PType | ForgeConnectorProductKey)[] = [];

export const useThirdPartyConfig = ({
	cloudId,
	absoluteUrl,
	userId,
	disabled,
	experience,
	analytics,
}: Partial<TSearch3pConfigProps> & {
	experience?: SearchStoreExperience;
	analytics: UseFetchConfigAnalytics;
}): {
	productKeys?: (ProductKeys3PType | ForgeConnectorProductKey)[];
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
} => {
	const [config, setConfig] = useState<{
		productKeys?: (ProductKeys3PType | ForgeConnectorProductKey)[];
		thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	}>({});

	const {
		fireAnalyticsEvent,
		logException,
		searchSessionId,
		source,
		actionSubject,
		taskNamePrefix,
		createNetworkCallbacks,
	} = analytics;

	useEffect(() => {
		if (!config.productKeys) {
			if (!disabled && cloudId) {
				const taskStartAt = performance.now();
				const taskId = crypto.randomUUID();

				fireAnalyticsEvent(
					onTaskStart({
						searchSessionId,
						taskId,
						task: buildTaskName(taskNamePrefix, CONFIG_TASK_SUFFIXES.THIRD_PARTY_CONFIG_UX),
						moreContext: { experience },
					}),
				);

				const networkCallbacks = createNetworkCallbacks
					? createNetworkCallbacks(
							taskId,
							buildTaskName(taskNamePrefix, CONFIG_TASK_SUFFIXES.THIRD_PARTY_CONFIG_NETWORK),
							searchSessionId,
							fireAnalyticsEvent,
							taskStartAt,
							{ experience },
						)
					: undefined;

				const degradationCallback: DegradationCallback = (reason) => {
					fireAnalyticsEvent(
						onDegradedDependency({
							dependency: 'search-3p-config',
							searchSessionId,
							reason,
							source,
							actionSubject,
						}),
					);
				};

				const cacheAnalyticsCallback: CacheAnalyticsCallback = async (state) => {
					fireAnalyticsEvent(
						onRequestCacheAccess({
							requestName: 'search-3p-config',
							searchSessionId,
							cacheHit: state.hit,
							refreshTriggered: state.refreshTriggered,
							source,
							actionSubject,
						}),
					);
				};

				get3pProductKeys({
					cloudId,
					absoluteUrl,
					userId,
					degradationCallback,
					cacheAnalyticsCallback,
					networkCallbacks,
				})
					.then((data) => {
						setConfig({
							productKeys: data.productKeys,
							thirdPartyConfigs: data.thirdPartyConfigs,
						});

						fireAnalyticsEvent(
							onTaskSuccess({
								searchSessionId,
								taskId,
								task: buildTaskName(taskNamePrefix, CONFIG_TASK_SUFFIXES.THIRD_PARTY_CONFIG_UX),
								moreContext: { timeTaken: performance.now() - taskStartAt, experience },
							}),
						);
					})
					.catch((error) => {
						fireAnalyticsEvent(
							onFailedDependency({
								dependency: 'search-3p-config',
								searchSessionId,
								source,
								actionSubject,
							}),
						);

						fireAnalyticsEvent(
							onTaskFail({
								searchSessionId,
								taskId,
								task: buildTaskName(taskNamePrefix, CONFIG_TASK_SUFFIXES.THIRD_PARTY_CONFIG_UX),
								moreContext: {
									error: error.name,
									timeTaken: performance.now() - taskStartAt,
									experience,
								},
							}),
						);

						setConfig({
							productKeys: EMPTY_RESPONSE,
							thirdPartyConfigs: {},
						});

						logException(error);
					});
			} else {
				setConfig({
					productKeys: EMPTY_RESPONSE,
					thirdPartyConfigs: {},
				});
			}
		}
	}, [
		cloudId,
		absoluteUrl,
		userId,
		disabled,
		config,
		logException,
		fireAnalyticsEvent,
		searchSessionId,
		source,
		actionSubject,
		taskNamePrefix,
		createNetworkCallbacks,
		experience,
	]);

	return config;
};
